import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Plus, Check, Loader } from "lucide-react";
import { Input } from "./ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
const CreateRequestCategoryDialog = React.lazy(() => import("./request-gallery/CreateRequestCategoryDialog"));
import { FieldLabel } from "./ui/field";
import { useRequestCategories } from "@/hooks/useRequestCategories";
import { useGroups } from "@/hooks/useGroups";
import { ICompasAIChatAction } from "@/types/compass.types";
import { useAnonymizePrompt } from "@/hooks/useAnonymizePrompt";
import { useChatMessagesChain } from "@/hooks/useChatMessagesChain";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

const formSchema = z.object({
  categoryId: z.string().optional(),
  groups: z.array(z.string()),
});

type FormValues = z.infer<typeof formSchema>;

interface OneClickAutomationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  actions: ICompasAIChatAction[];
  documentId: string;
}

const OneClickAutomationDialog: React.FC<OneClickAutomationDialogProps> = ({
  isOpen,
  onClose,
  actions,
  documentId,
}) => {
  const [anonymizedActions, setAnonymizedActions] = React.useState<ICompasAIChatAction[]>([]);
  const [isAnonymizing, setIsAnonymizing] = React.useState(false);
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = React.useState(false);
  const [searchGroup, setSearchGroup] = React.useState("");

  const { data: categories, isLoading: isCategoriesLoading } = useRequestCategories();
  const { data: groupsData, isLoading: isGroupsLoading } = useGroups();

  const anonymizePromptMutation = useAnonymizePrompt();

  const chatMessagesChainMutation = useChatMessagesChain();

  const queryClient = useQueryClient();

  const { control, handleSubmit, setValue, watch, reset } = useForm<FormValues>({
    resolver: zodResolver(formSchema),

    defaultValues: {
      categoryId: "",
      groups: [],
    },
  });

  const selectedGroups = watch("groups");

  React.useEffect(() => {
    if (isOpen && actions.length > 0) {
      handleAnonymizeActions();
    } else {
      setAnonymizedActions([]);
      reset({ categoryId: "", groups: [] });
    }
  }, [isOpen, actions, reset]);

  const handleAnonymizeActions = async () => {
    setIsAnonymizing(true);
    try {
      const promises = actions.map((action) =>
        anonymizePromptMutation.mutateAsync({
          prompt: action.prompt,
          preserve_context: true,
        })
      );

      const results = await Promise.all(promises);

      const newActions = actions.map((action, index) => ({
        ...action,
        prompt: results[index]?.anonymized_prompt || action.prompt,
      }));

      setAnonymizedActions(newActions);
    } catch (error) {
      console.error("Failed to anonymize actions:", error);
      toast.error("Failed to anonymize actions");
      // Fallback to original actions
      setAnonymizedActions(actions);
    } finally {
      setIsAnonymizing(false);
    }
  };

  const toggleGroup = (groupId: string) => {
    const currentGroups = selectedGroups || [];
    const newGroups = currentGroups.includes(groupId)
      ? currentGroups.filter((id) => id !== groupId)
      : [...currentGroups, groupId];
    setValue("groups", newGroups);
  };

  const onSubmit = async (data: FormValues) => {
    if (!actions.length || !actions[0]) return;

    // We assume all actions belong to the same message/thread context
    const messageId = actions[0].message_id;
    if (!messageId) {
      toast.error("Message ID missing from actions");
      return;
    }

    try {
      await chatMessagesChainMutation.mutateAsync({
        message_id: messageId,
        context_id: documentId,
        type: "discuss",
        category_id: data.categoryId || null,
        groups: data.groups,
      });

      queryClient.invalidateQueries({ queryKey: ["requests"] });
      toast.success("1-Click Automation created successfully");
      onClose();
    } catch (error) {
      console.error("Failed to create automation:", error);
      toast.error("Failed to create 1-Click Automation");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] h-[80vh] p-0 flex flex-col gap-0">
        <DialogHeader className="px-4 py-3 border-b">
          <DialogTitle className="text-base font-medium">Create 1-Click Automation</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-hidden p-3 flex flex-col gap-4">
            {/* Category Selection */}

            <div className="space-y-1 flex-shrink-0">
              <div className="flex items-center justify-between">
                <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
                  Category
                </FieldLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        className="size-6"
                        size={"icon-sm"}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsCreateCategoryOpen(true);
                        }}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-[10px]">Create new category</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="w-full h-8 text-xs">
                      <SelectValue placeholder="Select a Category" />
                    </SelectTrigger>

                    <SelectContent>
                      {isCategoriesLoading ? (
                        <div className="p-2 text-xs text-center">Loading...</div>
                      ) : (
                        categories

                          ?.filter((cat) => !cat.parent_id)

                          .map((category) => (
                            <SelectItem key={category._id} value={category._id} className="text-xs">
                              {category.name}
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Actions List */}

            <div className="space-y-1 flex-1 flex flex-col min-h-0">
              <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
                Action(s)
              </FieldLabel>

              {isAnonymizing ? (
                <div className="flex flex-col items-center justify-center flex-1 border rounded-md border-dashed bg-muted py-8">
                  <Loader className="animate-spin text-primary-400 mb-2" />

                  <span className="text-xs text-neutral-500">Anonymizing actions...</span>
                </div>
              ) : (
                <div className="flex-1 border rounded-md bg-muted min-h-[120px] overflow-y-auto">
                  <div className="p-2 space-y-2">
                    {anonymizedActions.map((action, idx) => (
                      <div key={idx} className="bg-card p-2 rounded border text-xs">
                        <div className="font-medium mb-0.5">
                          {action.heading || action.label || `Action ${idx + 1}`}
                        </div>

                        <div className="text-neutral-500 line-clamp-2 font-normal">
                          {action.prompt}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Groups Selection */}

            <div className="space-y-1 flex-shrink-0">
              <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
                Search Group to share
              </FieldLabel>
              <Input
                placeholder="Enter group name"
                className="h-8 text-xs mb-2"
                value={searchGroup}
                onChange={(e) => setSearchGroup(e.target.value)}
              />

              <div className="border rounded-md">
                <div className="p-2 flex flex-wrap gap-2">
                  {isGroupsLoading ? (
                    <p className="text-xs text-neutral-400">Loading groups...</p>
                  ) : (
                    groupsData?.data
                      .filter((group) => group.name.toLowerCase().includes(searchGroup.toLowerCase()))
                      .map((group) => {
                        const isSelected = selectedGroups?.includes(group.id);

                        return (
                          <Badge
                            key={group.id}
                            variant="outline"
                            onClick={() => toggleGroup(group.id)}
                            className={cn(
                              "rounded-md text-neutral-500 cursor-pointer text-xs h-6 px-2 font-normal",

                              isSelected && "bg-primary-300 text-neutral-white border-primary-300"
                            )}
                          >
                            {isSelected && <Check className="w-3 h-3 mr-1" />}

                            {group.name}
                          </Badge>
                        );
                      })
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-4 py-3 border-t flex flex-row items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>

            <Button
              type="submit"
              size="sm"
              disabled={
                isAnonymizing || chatMessagesChainMutation.isPending || actions.length === 0
              }
            >
              {chatMessagesChainMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
        <React.Suspense fallback={<Loader className="animate-spin" />}>
          <CreateRequestCategoryDialog
            open={isCreateCategoryOpen}
            onOpenChange={setIsCreateCategoryOpen}
          />
        </React.Suspense>
      </DialogContent>
    </Dialog>
  );
};

export default OneClickAutomationDialog;
